import { css } from '@emotion/react'
import {
    FunctionComponent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import { VList, VListHandle } from 'virtua'
import { clearAllPreviews } from '../../../app-state/modals/state.floater'
import {
    resetAvatarInfoOpenIDState,
    resetExploreScrollPositions,
} from '../../../app-state/state.grid'
import {
    DisplayUeopt,
    GridLocation,
    SettingsType,
} from '../../../constants/enums'
import { GridDensityUeopt } from '../../../mfcLogic/ueopts'
import { APPSTATE } from '../../../state/state.app'
import { AppRouter, HISTORY } from '../../../state/state.router'
import { isTouchDevice } from '../../../utilities/browser'
import { safeObj } from '../../../utilities/safe'
import ButtonDisplayType from '../../Buttons/ButtonDisplayType'
import { getPassiveState, usePassive } from '../../Hooks/usePassive'
import { usePreservePageState } from '../../Hooks/usePreservePageState'
import { useRouteSelector } from '../../Hooks/useRoute'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { useSettingSelector } from '../../Hooks/useSettings'
import {
    GetStylesConfig,
    getDisplayHeight,
    getGridPadding,
    getGridSpecs,
    getScale,
    getStyle,
} from './helpers'
import { useKeyboardNavigation } from './useKeyboardNavigation'

const noOverflowY = css({
    overflowY: 'hidden',
})

interface RowProps {
    index: number
    data: any
}

const Row = memo((props: RowProps) => {
    const { index, data } = props
    const { rowComponentProps, RowComponent, padding } = data

    if (padding) {
        const paddingHeight = typeof padding === 'number' ? padding : 30
        return <div style={{ height: paddingHeight }} />
    }

    return (
        <div
            className="no-scrollbars"
            css={noOverflowY}
            data-testid={
                rowComponentProps.category
                    ? `Row__${rowComponentProps?.category}`
                    : `Row__${index}`
            }
        >
            <RowComponent {...rowComponentProps} index={index} />
        </div>
    )
})

interface OnScrollProps {
    scrollOffset: number
    averageRowHeight: number
    greatestRowHeight: number
    totalRowsHeight: number
    containerHeight: number
    rowCount: number
}

interface CoreProps {
    containerHeight: number
    containerWidth: number
    enableOnScroll?: boolean
    displayHeight: number
    gridDensity: GridDensityUeopt
    gridLocation?: GridLocation
    gridPadding: number
    columnWidth: number
    columnCount: number
    DisplayTypeButton?: FunctionComponent<any>
    displayTypeButtonProps?: any
    platform: 'mobile' | 'desktop'
    onScroll?: (props: OnScrollProps) => void
    getStyle: (key: string, config?: GetStylesConfig) => any
    getRows: (info: any) => any
    display: string
    scale: 'sm' | 'md' | 'lg'
    data: any
}

const Core = memo((props: CoreProps) => {
    const {
        DisplayTypeButton = ButtonDisplayType,
        displayTypeButtonProps = {},
        containerHeight,
        containerWidth,
        enableOnScroll,
        displayHeight,
        gridDensity,
        gridLocation,
        gridPadding,
        columnWidth,
        columnCount,
        platform,
        onScroll,
        getStyle,
        getRows,
        display,
        scale,
        data,
    } = props

    const currentRoute = useRouteSelector('visibleRoute')
    const previousDisplay = useRef(display)
    const refScrollTop = useRef<number>(0)
    const listRef = useRef<VListHandle>(null)

    const { positionTop } = safeObj(
        usePreservePageState({
            gridLocation,
            refScrollTop,
            displayMode: display as DisplayUeopt,
            disablePreserveScrollPosition: gridLocation !== GridLocation.Home,
        }),
    )

    const rows = useMemo(
        () =>
            getRows({
                containerHeight,
                containerWidth,
                displayHeight,
                gridPadding,
                columnWidth,
                columnCount,
                platform,
                getStyle,
                display,
                scale,
                data,
            }),
        [
            containerHeight,
            containerWidth,
            displayHeight,
            gridPadding,
            columnWidth,
            columnCount,
            platform,
            getStyle,
            display,
            getRows,
            scale,
            data,
        ],
    )

    useEffect(() => {
        if (!listRef.current || getPassiveState()) {
            return
        }
        const previousRoute = HISTORY.lastRoute[HISTORY.lastRoute.length - 1]
        const currentPage = AppRouter.route.visiblePage

        // eslint-disable-next-line fp/no-let
        let scrollToTop = false
        if (previousRoute.visibleRoute !== currentRoute) {
            if (
                !positionTop &&
                // don't scroll down if we're navigating to a room
                !(
                    currentPage === 'room' ||
                    currentPage === 'message' ||
                    currentPage === 'profile'
                )
            ) {
                scrollToTop = true
            }
        }

        if (previousDisplay.current !== display) {
            previousDisplay.current = display
            scrollToTop = true
        }

        if (scrollToTop) {
            listRef.current.scrollTo(0)
        }
    }, [currentRoute, columnWidth, columnCount, gridPadding, display, rows])

    const totalRowsHeight = useMemo(() => {
        return rows.reduce((memo, { rowHeight = 0 }) => memo + rowHeight, 0)
    }, [rows])

    const greatestRowHeight = useMemo(() => {
        return rows.reduce((memo, { rowHeight = 0 }) => {
            if (rowHeight > memo) {
                return rowHeight
            }
            return memo
        }, 0)
    }, [rows])

    const rowCount = rows.length

    const averageRowHeight = useMemo(() => {
        if (!rowCount) {
            if (totalRowsHeight) {
                return totalRowsHeight
            }

            return 0
        }

        return totalRowsHeight && rowCount && totalRowsHeight / rowCount
    }, [totalRowsHeight, rowCount])

    const rowComponents = useMemo(
        () =>
            rows.map((row, index) => (
                <Row key={row.itemKey || index} index={index} data={row} />
            )),
        [rows],
    )

    // scroll grid up/down using arrow keys, page up/down, home/end
    useKeyboardNavigation({
        listRef,
        averageRowHeight,
        containerHeight,
        totalRowsHeight,
    })

    const onScrollCallback = useCallback(
        (scrollOffset: number) => {
            refScrollTop.current = scrollOffset

            if (typeof onScroll !== 'function' || !enableOnScroll) {
                return
            }

            onScroll({
                scrollOffset,
                greatestRowHeight,
                averageRowHeight,
                totalRowsHeight,
                containerHeight,
                rowCount,
            })
        },
        [
            greatestRowHeight,
            averageRowHeight,
            totalRowsHeight,
            containerHeight,
            enableOnScroll,
            rowCount,
            onScroll,
        ],
    )

    useEffect(() => {
        if (listRef.current && positionTop) {
            listRef.current.scrollTo(positionTop)
        }
    }, [])

    return (
        <>
            <VList
                className="live-preview-grid no-scrollbars"
                style={{ height: containerHeight, width: '100%' }}
                onScroll={onScrollCallback}
                overscan={2}
                ref={listRef}
            >
                {rowComponents}
            </VList>
            <DisplayTypeButton
                display={display}
                gridDensity={gridDensity}
                platform={platform}
                {...displayTypeButtonProps}
            />
        </>
    )
})

interface GridProps {
    displayOverride?: string
    enableOnScroll?: boolean
    enableSlide?: boolean
    gridDensityOverride?: GridDensityUeopt
    DisplayTypeButton?: FunctionComponent<any>
    displayTypeButtonProps?: any
    gridLocation?: GridLocation
    overrideHeight?: number
    onScroll?: (props: OnScrollProps) => void
    getRows: (info: any) => any
    data: any
}

const Grid = memo((props: GridProps) => {
    const {
        gridDensityOverride,
        displayOverride,
        DisplayTypeButton,
        displayTypeButtonProps,
        enableOnScroll,
        enableSlide,
        gridLocation,
        overrideHeight,
        onScroll,
        getRows,
        data,
    } = props
    const [displayUeopt] = useSettingSelector(SettingsType.DEFAULT, 'display')
    const [gridDensityUeopt] = useSettingSelector(
        SettingsType.DEFAULT,
        'gridDensity',
    )
    const display = displayOverride ?? displayUeopt
    const gridDensity = gridDensityOverride ?? gridDensityUeopt
    const { type: view } = useRouteSelector('baseQuery')
    const mobileView = APPSTATE.app.isMobile // this is safe because this never changes after initial load
    const isExploreView = gridLocation === GridLocation.Search && !view
    const scale = getScale(gridDensity)
    const gridPadding = getGridPadding(scale)
    const contentWidth = useScreenStateSelector('contentWidth')
    const contentHeight = useScreenStateSelector('contentHeight')
    const windowHeight = useScreenStateSelector('windowHeight')

    const passiveMode = usePassive()
    const usableHeight =
        // friends and rooms have grids that aren't full height, so we should use the passed in override height
        overrideHeight
            ? overrideHeight
            : passiveMode
              ? windowHeight
              : contentHeight
    const usableWidth = data.activeRoom ? data.roomWidth : contentWidth
    useEffect(() => {
        resetAvatarInfoOpenIDState()
        resetExploreScrollPositions()
        // setLargePreviewPinnedIDState(null)
        clearAllPreviews()
        return () => {
            resetAvatarInfoOpenIDState()
            resetExploreScrollPositions()
            // setLargePreviewPinnedIDState(null)
            clearAllPreviews()
        }
    }, [])

    useEffect(() => {
        resetExploreScrollPositions()
    }, [isExploreView])

    //
    // Marc (07/29/2023): I should probably include a tablet view:
    // (!isMobile() && isTouchDevice)
    //
    const platform = mobileView ? 'mobile' : 'desktop'
    const { columnWidth, columnCount } = getGridSpecs({
        isTouchDevice: isExploreView ? isTouchDevice : false,
        gridPadding,
        enableSlide,
        platform,
        display,
        scale,
        width: usableWidth,
    })
    const displayHeight = getDisplayHeight({
        columnWidth: columnWidth - gridPadding,
        display,
    })
    const styles = getStyle(
        platform,
        {},
        {
            gridPadding,
            columnWidth,
            columnCount,
        },
    )(scale)(display)

    // console.log('[Grid]', {
    //     usableWidth,
    //     // gridPadding,
    //     // columnWidth,
    //     // columnCount,
    //     displayHeight,
    //     gridLocation,
    // })
    return (
        <div
            style={{
                width: usableWidth,
                height: overrideHeight,
                position: 'relative',
                overflow: 'hidden',
            }}
            id="grid-root"
        >
            <Core
                getStyle={styles}
                enableOnScroll={enableOnScroll}
                displayHeight={displayHeight}
                DisplayTypeButton={DisplayTypeButton}
                displayTypeButtonProps={displayTypeButtonProps}
                gridDensity={gridDensity}
                gridLocation={gridLocation}
                gridPadding={gridPadding}
                columnWidth={columnWidth}
                columnCount={columnCount}
                containerHeight={usableHeight}
                containerWidth={usableWidth}
                platform={platform}
                onScroll={onScroll}
                getRows={getRows}
                display={display}
                scale={scale}
                data={data}
            />
        </div>
    )
})

export default Grid
export { Grid }
